/**
 * Map theme registry.
 *
 * A map theme changes the look and feel of the canvas: the background,
 * the nodes, the connection lines, and the node font. The active theme is
 * stored per map (in the map's shared Yjs `meta` map under the `theme`
 * key) so every collaborator sees the same look, and it persists with the
 * map. Themes are a pure rendering layer: switching themes never rewrites
 * stored node colors, so switching back always restores the previous look.
 *
 * Each theme may also define its own node color palette, which the color
 * picker offers while that theme is active (e.g. chalk pastels on the
 * chalkboard, warm hues for a future warm theme).
 */

import { COLORS } from './colors';

export const DEFAULT_THEME_ID = 'default';

const DEFAULT_NODE_FONT_FAMILY = 'Arial, sans-serif';

/**
 * Extract the first hex color from a stored node color value
 * (usually a CSS gradient string, occasionally a bare hex).
 */
export function extractFirstHex(colorValue) {
  if (typeof colorValue !== 'string') return null;
  const match = colorValue.match(/#[0-9a-fA-F]{6}/);
  return match ? match[0] : null;
}

function hexToRgb(hex) {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

/** Mix a color toward white; amount 0 keeps the color, 1 is pure white. */
function mixTowardWhite({ r, g, b }, amount) {
  return {
    r: Math.round(r + (255 - r) * amount),
    g: Math.round(g + (255 - g) * amount),
    b: Math.round(b + (255 - b) * amount),
  };
}

/**
 * Turn a stored node color into a "colored in with chalk" fill: the
 * node's hue, lifted toward pastel, shaded so the middle looks pressed
 * on harder than the edges. Works for any existing map because it only
 * needs one hex from the stored gradient.
 */
export function chalkFillFromColor(colorValue) {
  const hex = extractFirstHex(colorValue);
  const base = hex ? hexToRgb(hex) : { r: 244, g: 241, b: 232 };
  const chalk = mixTowardWhite(base, 0.55);
  const { r, g, b } = chalk;
  return (
    `radial-gradient(ellipse at 50% 42%, rgba(${r}, ${g}, ${b}, 0.52) 0%, ` +
    `rgba(${r}, ${g}, ${b}, 0.30) 62%, rgba(${r}, ${g}, ${b}, 0.16) 100%)`
  );
}

// Chalk pastels offered by the color picker while the chalkboard theme is
// active. Stored as ordinary gradients so they render sensibly if the map
// later switches back to another theme.
export const CHALKBOARD_PALETTE = [
  { name: 'Chalk White', gradient: 'linear-gradient(135deg, #f4f1e8 0%, #e6e2d4 100%)' },
  { name: 'Chalk Yellow', gradient: 'linear-gradient(135deg, #f5e6a8 0%, #ecd88a 100%)' },
  { name: 'Chalk Pink', gradient: 'linear-gradient(135deg, #f2c4cf 0%, #e8aebd 100%)' },
  { name: 'Chalk Blue', gradient: 'linear-gradient(135deg, #aecbe8 0%, #93b7dd 100%)' },
  { name: 'Chalk Green', gradient: 'linear-gradient(135deg, #b8d8bc 0%, #9fc7a5 100%)' },
  { name: 'Chalk Orange', gradient: 'linear-gradient(135deg, #f4cfa5 0%, #ecbd85 100%)' },
  { name: 'Chalk Lavender', gradient: 'linear-gradient(135deg, #cdc1e8 0%, #b7a8dd 100%)' },
  { name: 'Chalk Mint', gradient: 'linear-gradient(135deg, #b2e0d4 0%, #97d1c1 100%)' },
  { name: 'Chalk Red', gradient: 'linear-gradient(135deg, #eaa9a0 0%, #e08f84 100%)' },
  { name: 'Chalk Grey', gradient: 'linear-gradient(135deg, #d0d3d1 0%, #b9bdbb 100%)' },
];

const CHALK_LINE = 'rgba(240, 240, 234, 0.72)';
const CHALK_LINE_SELECTED = 'rgba(255, 255, 255, 0.95)';
const CHALK_TEXT = 'rgba(250, 250, 245, 0.96)';
const CHALK_BORDER = 'rgba(245, 245, 238, 0.85)';

// Slate green-black with a faint dusting of chalk residue.
const CHALKBOARD_BACKGROUND =
  'radial-gradient(ellipse at 18% 22%, rgba(255, 255, 255, 0.045) 0%, rgba(255, 255, 255, 0) 42%), ' +
  'radial-gradient(ellipse at 78% 70%, rgba(255, 255, 255, 0.035) 0%, rgba(255, 255, 255, 0) 46%), ' +
  'radial-gradient(ellipse at 45% 95%, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 38%), ' +
  'linear-gradient(160deg, #2b2f2c 0%, #242825 55%, #202421 100%)';

/**
 * Theme contract:
 * - id / name / description: identity and menu copy.
 * - canvas.getBackground(isDarkMode): CSS background for the map canvas
 *   (and thumbnails). The default theme follows the light/dark toggle;
 *   opinionated themes may ignore it.
 * - canvas.getSolidColor(isDarkMode): plain fallback color for surfaces
 *   that cannot render a CSS gradient (image export, thumbnails).
 * - node.fontFamily: node text font (also used when measuring node text);
 *   null keeps the app default.
 * - node.getStyle(ctx): style overrides merged over the standard node
 *   style, or null for no change. ctx: { color, isRoot, isSelected,
 *   isHighlighted, isDragging }.
 * - edge.getStyle(ctx): { stroke, strokeWidth } for connection lines, or
 *   null to keep the standard treatment. ctx: { isDarkMode, isSelected }.
 * - palette: node colors the picker offers while this theme is active.
 * - preview: colors for the theme switcher swatch.
 */
export const MAP_THEMES = {
  [DEFAULT_THEME_ID]: {
    id: DEFAULT_THEME_ID,
    name: 'Default',
    description: 'The classic Mindmappi look.',
    canvas: {
      getBackground: (isDarkMode) => (isDarkMode ? '#1a1a1a' : '#ffffff'),
      getSolidColor: (isDarkMode) => (isDarkMode ? '#1a1a1a' : '#ffffff'),
    },
    node: {
      fontFamily: null,
      getStyle: () => null,
    },
    edge: {
      getStyle: () => null,
    },
    palette: COLORS,
    preview: {
      background: '#ffffff',
      node: COLORS[0].gradient,
      nodeText: '#ffffff',
      border: '#d1d5db',
    },
  },

  chalkboard: {
    id: 'chalkboard',
    name: 'Chalkboard',
    description: 'White chalk on a dark board.',
    canvas: {
      getBackground: () => CHALKBOARD_BACKGROUND,
      getSolidColor: () => '#242825',
    },
    node: {
      fontFamily: "'Patrick Hand', 'Segoe Print', 'Comic Sans MS', cursive",
      getStyle: ({ color, isSelected, isHighlighted, isDragging }) => ({
        background: chalkFillFromColor(color),
        border: `2px solid ${CHALK_BORDER}`,
        color: CHALK_TEXT,
        // Slightly uneven corners read as hand-drawn without any filters.
        borderRadius: '46px 38px 44px 40px / 38px 44px 40px 46px',
        boxShadow: (isSelected || isDragging)
          ? '0 0 14px rgba(255, 255, 255, 0.28), 0 0 2px rgba(255, 255, 255, 0.4)'
          : isHighlighted
            ? '0 0 0 3px rgba(255, 255, 255, 0.35), 0 0 10px rgba(255, 255, 255, 0.18)'
            : '0 0 6px rgba(255, 255, 255, 0.08)',
      }),
    },
    edge: {
      getStyle: ({ isSelected }) => ({
        stroke: isSelected ? CHALK_LINE_SELECTED : CHALK_LINE,
        strokeWidth: isSelected ? 3 : 2.25,
      }),
    },
    palette: CHALKBOARD_PALETTE,
    preview: {
      background: '#262a27',
      node: 'transparent',
      nodeText: CHALK_TEXT,
      border: CHALK_BORDER,
    },
  },
};

export const MAP_THEME_LIST = Object.values(MAP_THEMES);

/** Resolve a theme id to a theme, falling back to the default theme. */
export function getMapTheme(themeId) {
  return MAP_THEMES[themeId] || MAP_THEMES[DEFAULT_THEME_ID];
}

/** CSS background for the map canvas under a theme. */
export function getThemeCanvasBackground(theme, isDarkMode) {
  return theme?.canvas?.getBackground
    ? theme.canvas.getBackground(isDarkMode)
    : (isDarkMode ? '#1a1a1a' : '#ffffff');
}

/** Solid fallback color for exports/thumbnails under a theme. */
export function getThemeCanvasSolidColor(theme, isDarkMode) {
  return theme?.canvas?.getSolidColor
    ? theme.canvas.getSolidColor(isDarkMode)
    : (isDarkMode ? '#1a1a1a' : '#ffffff');
}

/** Node text font for a theme, for both rendering and text measurement. */
export function getThemeNodeFontFamily(theme) {
  return theme?.node?.fontFamily || DEFAULT_NODE_FONT_FAMILY;
}

/** Node style overrides for a theme, or null when the theme keeps defaults. */
export function getThemeNodeStyle(theme, context) {
  if (!theme?.node?.getStyle) return null;
  return theme.node.getStyle(context) || null;
}

/** Edge style overrides for a theme, or null when the theme keeps defaults. */
export function getThemeEdgeStyle(theme, context) {
  if (!theme?.edge?.getStyle) return null;
  return theme.edge.getStyle(context) || null;
}
